// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@plumberssupply/App/Components/Notify";
import TitleTypography, { TitleTypographyProps } from "@plumberssupply/App/Components/TitleTypography/TitleTypography";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import ProductCarouselProduct from "@plumberssupply/Overrides/Components/ProductCarouselProduct";
import { MappedLink } from "@plumberssupply/Overrides/Widgets/Header/MainNavigation";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    recentlyPurchasedPageUrl?: string;
    isLoading?: boolean;
}

type Props = OwnProps;

interface RecentlyPurchasedStyle {
    titleText?: TitleTypographyProps;
    centerWrapper?: InjectableCss;
    container?: InjectableCss;
    itemsGridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    notify?: InjectableCss;
}

const styles: RecentlyPurchasedStyle = {
    centerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: 20px 0;
        `,
    },
    titleText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    && {
                        margin-bottom: 0;
                    }
                `,
            },
            title: {
                css: css`
                    && {
                        font-size: 14px;
                        font-weight: 700;
                        color: ${color("common.ui11")};
                        line-height: 18px;
                    }
                `,
            },
            link: {
                typographyProps: {
                    css: css`
                        && {
                            font-size: 12px;
                            line-height: 15px;
                        }
                    `,
                },
            },
        },
    },
    container: {
        css: css`
            width: 100%;
            min-width: 600px;
            background-color: ${color("common.white")};
        `,
    },
    itemsGridContainer: {
        offsetCss: css`
            > div:nth-child(-n + 3) {
                border-bottom: 1px solid ${color("common.ui7")};
            }
        `,
    },
    gridItem: {
        css: css`
            padding: 23px 30px 23px 0;
            min-width: 400px;

            ${xScreen("lte", 1250)} {
                min-width: 350px;
            }
        `,
    },
    notify: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const RecentlyPurchased: FC<Props> = ({ recentlyPurchasedPageUrl, isLoading }) => {
    const { products, isTabletScreen } = useAppSelector(state => ({
        recentlyPurchasedUrl: getPageLinkByPageType(state, "RecentlyPurchasedPage")?.url,
        products: getProductsForProductInfoList(state, HeaderRecentlyPurchasedWidgetId),
        isTabletScreen: getXngCommon(state).isTabletScreen,
    }));

    return (
        <StyledWrapper {...styles.container}>
            {products && products.length > 0 ? (
                <>
                    <TitleTypography
                        {...styles.titleText}
                        link={recentlyPurchasedPageUrl}
                        linkText={translate("View All Previously Purchased")}
                    >
                        {translate(`Displaying 1-${products.length}`)}
                    </TitleTypography>
                    <GridContainer gap={0} {...styles.itemsGridContainer}>
                        {products.map(product => (
                            <GridItem key={product.id} width={isTabletScreen ? 6 : 4} {...styles.gridItem}>
                                <ProductCarouselProduct
                                    key={product.id}
                                    carouselId={HeaderRecentlyPurchasedWidgetId}
                                    product={product}
                                    showImage={true}
                                    showBrand={true}
                                    showTitle={true}
                                    showPartNumbers={true}
                                    showPrice={true}
                                    showAddToCart={true}
                                    showAddToList={true}
                                />
                            </GridItem>
                        ))}
                    </GridContainer>
                </>
            ) : (
                <>
                    {isLoading ? (
                        <StyledWrapper {...styles.centerWrapper}>
                            <LoadingSpinner />
                        </StyledWrapper>
                    ) : (
                        <Notify type="info" {...styles.notify}>
                            {translate("No products found")}
                        </Notify>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

export default RecentlyPurchased;

export const HeaderRecentlyPurchasedWidgetId = "header-RecentlyPurchased";
